import {useEffect, useMemo, useState} from "react";
import {Link, useNavigate, useParams} from "react-router";
import {ArrowLeft, ExternalLink, Pen, Plus, Trash2} from "lucide-react";
import {isAxiosError} from "axios";
import {useCampaigns} from "@/campaigns/CampaignHooks.ts";
import type Campaign from "@/campaigns/model/Campaign.ts";
import CampaignCard from "@/campaigns/CampaignCard.tsx";
import {deleteCampaign, getCampaignBySlug} from "@/api/CampaignApi.ts";
import {srcUrl} from "@/api/ImageApi.ts";
import SearchBar from "@/search/SearchBar.tsx";
import {Button} from "@/components/ui/button.tsx";
import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import CampaignMembersPanel from "@/admin/campaigns/CampaignMembersPanel.tsx";
import {notifyError} from "@/api/ErrorNotification.ts";

export default function CampaignManagementView() {
    const {campaignSlug} = useParams();
    return campaignSlug ? <CampaignManagementDetails key={campaignSlug} campaignSlug={campaignSlug}/>
        : <CampaignManagementOverview/>;
}

function CampaignManagementOverview() {
    const {campaigns, loading, error, retry} = useCampaigns();
    const [query, setQuery] = useState("");
    const filtered = useMemo(() => {
        const normalized = query.trim().toLocaleLowerCase("de-DE");
        return normalized
            ? campaigns.filter(campaign => campaign.title.toLocaleLowerCase("de-DE").includes(normalized))
            : campaigns;
    }, [campaigns, query]);

    return <main className="min-h-full bg-background p-6 md:p-10">
        <title>Kampagnen verwalten | DireDawn</title>
        <div className="mx-auto max-w-5xl space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Administration</p>
                    <h1 className="text-3xl font-semibold tracking-tight">Kampagnen</h1>
                    <p className="mt-2 text-muted-foreground">Kampagnen verwalten und Mitglieder zuordnen.</p>
                </div>
                <Button asChild><Link to="/admin/campaigns/new"><Plus/>Neue Kampagne</Link></Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="w-full max-w-sm"><SearchBar value={query} onChange={setQuery} placeholder="Kampagnen suchen"/></div>
                {!loading && !error && <p className="text-sm text-muted-foreground">
                    {query.trim() ? `${filtered.length} von ${campaigns.length} Kampagnen` : `${campaigns.length} Kampagnen`}
                </p>}
            </div>
            {loading && <p role="status" className="text-muted-foreground">Kampagnen werden geladen…</p>}
            {error && <div role="alert" className="flex flex-wrap items-center gap-3 text-red-400">
                <span>Kampagnen konnten nicht geladen werden.</span>
                <Button variant="outline" size="sm" onClick={retry}>Erneut laden</Button>
            </div>}
            {!loading && !error && filtered.length === 0 && <div className="rounded-xl border border-border bg-card/60 p-8 text-center text-muted-foreground">
                {campaigns.length === 0 ? "Noch keine Kampagnen vorhanden. Lege deine erste Kampagne an."
                    : "Keine Kampagne entspricht der Suche."}
            </div>}
            {!loading && !error && filtered.length > 0 && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map(campaign => <CampaignCard key={campaign.id} campaign={campaign}
                    to={`/admin/campaigns/${campaign.slug}`}/>)}
            </div>}
        </div>
    </main>;
}

function CampaignManagementDetails({campaignSlug}: {campaignSlug: string}) {
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState<Campaign>();
    const [error, setError] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getCampaignBySlug(campaignSlug)
            .then(value => {
                if (!cancelled) setCampaign(value);
            })
            .catch(() => {
                if (!cancelled) setError("Die Kampagne konnte nicht geladen werden.");
            });
        return () => { cancelled = true; };
    }, [campaignSlug]);

    const handleDelete = async () => {
        if (!campaign) return;
        setDeleting(true);
        setError("");
        try {
            await deleteCampaign(campaign.id);
            navigate("/admin/campaigns", {replace: true});
        } catch (reason) {
            if (isAxiosError(reason) && reason.response?.status === 409) {
                setError("Diese Kampagne enthält noch Encounter oder Szenen und kann nicht gelöscht werden.");
            } else notifyError(reason, "Die Kampagne konnte nicht gelöscht werden.");
        } finally {
            setDeleting(false);
        }
    };

    return <main className="min-h-full bg-background p-6 md:p-10">
        <title>{campaign ? `${campaign.title} verwalten | DireDawn` : "Kampagne verwalten | DireDawn"}</title>
        <div className="mx-auto max-w-5xl space-y-6">
            <Link to="/admin/campaigns" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="size-4"/>Alle Kampagnen
            </Link>
            {error && <p role="alert" className="text-red-400">{error}</p>}
            {!campaign && !error && <p role="status">Kampagne wird geladen…</p>}
            {campaign && <>
                <section className="overflow-hidden rounded-xl border border-border/80 bg-card/90 shadow-lg shadow-black/10 sm:flex">
                    <div className="relative grid w-64 h-40 shrink-0 place-items-center overflow-hidden bg-surface ">
                        <img src={srcUrl(campaign.image)} alt="" className="h-full w-full object-cover"/>
                        <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-card/50 to-transparent"/>
                    </div>
                    <div className="min-w-0 flex-1 space-y-4 p-5 sm:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Kampagne verwalten</p>
                                <h1 className="text-3xl font-semibold tracking-tight">{campaign.title}</h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <Button asChild variant="outline"><Link to={`/campaigns/${campaign.slug}`}><ExternalLink/>Öffnen</Link></Button>
                                <Button asChild variant="outline"><Link to={`/admin/campaigns/${campaign.slug}/edit`}><Pen/>Bearbeiten</Link></Button>
                                <DeleteConfirm title={`${campaign.title} löschen?`} onConfirm={handleDelete}>
                                    <Button variant="destructive" title="Löschen" disabled={deleting}><Trash2/>Löschen</Button>
                                </DeleteConfirm>
                            </div>
                        </div>
                        <p className="whitespace-pre-wrap text-muted-foreground">{campaign.description || "Keine Beschreibung vorhanden."}</p>
                    </div>
                </section>
                <CampaignMembersPanel campaign={campaign}/>
            </>}
        </div>
    </main>;
}
